import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Button,
  makeStyles,
  Grid,
} from "@material-ui/core";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { sendAttachments } from "../../../redux/actions";
import { Error } from "../../common/Error";
import { orgSelector } from "../../../redux/selectors";
import { isAllowedFile } from "../../common/utils/fileValidationUtil";

const useStyles = makeStyles((theme) => ({
  dialogActions: {
    padding: 15,
  },
  body: {
    marginTop: 5,
    marginBottom: 5,
  },
}));

type Props = {
  orgId: string;
  onClose: () => void;
};

export const AttachDialog: React.FC<Props> = ({ onClose, orgId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const orgState = useSelector(orgSelector);

  const { handleSubmit, control, errors } = useForm({
    defaultValues: { note: "", files: "" },
  });

  const handleSendMessage = (data: { files: File[]; note: string }) => {
    dispatch(sendAttachments(orgId, data.files, data.note));
    onClose();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="attach-doc-en-dialog"
    >
      <DialogTitle data-test="attach-doc-en-dialog-title">
        Attach Document
      </DialogTitle>
      <Divider component="div" />
      <DialogContent>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
              control={control}
              name="files"
              rules={{
                required: true,
                validate: (files: File[]) => {
                if (orgState.org?.pendingRecognition) {
                  if (!Array.isArray(files)) {
                  return false;
                  }
                  if (files.length > 5) {
                  return false;
                  }
                  return files.every(
                  (file) =>
                    file.size <= 2 * 1024 * 1024 && isAllowedFile(file)
                  );
                }
                },
              }}
              as={<FileInput multiple={true} />}
              />
              {errors.files && errors.files.type === "validate" && (
              <Error>
                &nbsp;&nbsp;You can attach up to 5 files, each up to 2MB and allowed type only
              </Error>
              )}
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={control}
                name="note"
                defaultValue={null}
                rules={{ required: true, maxLength: 5000 }}
                as={
                  <TextField
                    data-test="note"
                    required
                    multiline
                    rows={5}
                    label="Note"
                  />
                }
              />
              {errors.note && errors.note.type === "required" && (
                <Error>This field is required</Error>
              )}
              {errors.note && errors.note.type === "maxLength" && (
                <Error>Note must be less than 5000 characters</Error>
              )}
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="close-attach-doc-button"
        >
          Close
        </Button>
        <Button
          onClick={handleSubmit(handleSendMessage)}
          color="primary"
          data-test="confirm-attach-doc-button"
        >
          Attach
        </Button>
      </DialogActions>
    </Dialog>
  );
};
